/* eslint-disable @typescript-eslint/no-explicit-any */
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import {
  FraudData,
  PatientAccount,
  PhoneVerificationChannels,
  RescheduledOrNewReservedVisit,
  ReservedVisit,
  SelectedVisit,
  ServiceType,
  UtmParameters,
  VisitMethod,
  VisitType,
} from '@types';
import { PatientProfile } from '@qmd-types/patient';
import { LeadData } from '@qmd-types/onboarding';
import {
  AuthSlice,
  createAuthSlice,
  createFlagsSlice,
  createIntakeQuizSlice,
  createPatientSlice,
  FlagsSlice,
  IntakeQuizSlice,
  PatientSlice,
  DevToolsSlice,
  createDevToolsSlice,
} from './slices';
import { seedStore } from './seeds';
import { devtoolsOptions, persistOptions } from './utils';
import z from 'zod';

const MERGE_PATIENT_ACCOUNT = 'mergePatientAccount';
const RESET_STATE = 'resetState';
const SET_FRAUD_DATA = 'setFraudData';
const SET_HAS_ACCEPTED_TERMS = 'setHasAcceptedTerms';
const SET_LEAD_SESSION_ID = 'setLeadSessionId';
const SET_PATIENT_PASSWORD = 'setPatientPassword';
const SET_SEARCH_NEXT_AVAILABILITY = 'setShouldSearchNextAvailability';
const SET_SERVICE_TYPES = 'setServiceTypes';
const SET_RESERVED_VISIT = 'setReservedVisit';
const SET_SOONEST_AVAILABILITY_DATE = 'setSoonestAvailabilityDate';
const SET_STATE_CODE = 'setStateCode';
const SET_REFERRAL_CODE = 'setReferralCode';
const SET_UTM_PARAMETERS = 'setUtmParameters';
const SET_VERIFICATION_CHANNEL = 'setVerificationChannel';
const SET_VERIFICATION_CHANNELS = 'setVerificationChannels';
const SET_VERIFICATION_CODE = 'setVerificationCode';
const SET_VERIFICATION_TOKEN = 'setVerificationToken';
const SET_VISIT = 'setVisit';
const SET_VISIT_TYPE_ID = 'setVisitTypeId';

export interface ApplicationState {
  fraudData?: FraudData;
  hasAcceptedTerms: boolean;
  leadSessionId?: string;
  patientAccount?: PatientAccount;
  patientPassword?: string;
  reservedVisit?: ReservedVisit;
  shouldSearchNextAvailability?: boolean;
  serviceTypes: ServiceType[];
  soonestAvailabilityDate: string;
  referralCode?: string;
  /** US state code (e.g., "CA"). Used for state-gated visit eligibility and routing. */
  stateCode?: string;
  utmParameters?: UtmParameters;
  verificationChannel?: PhoneVerificationChannels | '';
  verificationChannels: PhoneVerificationChannels[];
  verificationCode?: string;
  verificationToken?: string;
  visit: SelectedVisit | null;
  visitTypeId: string | null;
}
// Type the schema to ensure it produces exactly ReservedVisit as output
// If ReservedVisit type changes, TypeScript will error here if the schema doesn't match
// The third parameter allows flexible input (with optional fields due to .default())
const ReservedVisitSchema: z.ZodType<ReservedVisit, z.ZodTypeDef, unknown> =
  z.object({
    allowedMethods: z.array(z.nativeEnum(VisitMethod)),
    expireDateTime: z.string(),
    price: z.number().default(0),
    reservationId: z.string(),
    consentForms: z
      .array(
        z.object({
          name: z.string(),
          url: z.string(),
          individualConsentRequired: z.boolean(),
        }),
      )
      .default([]),
    isFollowupVisitIncluded: z.boolean().default(false),
    isReferralAllowed: z.boolean().default(false),
    subscriptionBasedPrice: z.number().nullable().default(null),
    visitCreditAmount: z.number().default(0),
  });

interface ApplicationActions {
  clearSessionData: () => void;
  mergePatientAccount: (params: Partial<PatientAccount>) => void;
  resetState: () => void;
  seedStore: () => void;
  setFraudData: (data?: FraudData) => void;
  setHasAcceptedTerms: (accepted: boolean) => void;
  setLeadSessionId: (leadSessionId: string) => void;
  setPatientPassword: (patientPassword: string) => void;
  setReservedVisit: (visit: RescheduledOrNewReservedVisit | undefined) => void;
  setShouldSearchNextAvailability: (shouldSearch: boolean) => void;
  setServiceTypes: (serviceTypes: ServiceType[]) => void;
  setSoonestAvailabilityDate: (soonestAvailabilityDate: string) => void;
  setReferralCode: (referralCode: string) => void;
  setStateCode: (stateCode: string) => void;
  setUtmParameters: (utmParameters: UtmParameters) => void;
  setVerificationChannel: (channel: PhoneVerificationChannels | '') => void;
  setVerificationChannels: (channels: PhoneVerificationChannels[]) => void;
  setVerificationCode: (code: string) => void;
  setVerificationToken: (token: string) => void;
  setVisit: (visit: SelectedVisit | null) => void;
  setVisitTypeId: (visitTypeId: string | null) => void;
  getLeadData: () => LeadData | undefined;
  getPatientProfile: () => PatientAccount | PatientProfile | undefined;
  getSetupAccountState: () => {
    hasAcceptedTerms: boolean;
    patientAccount?: PatientAccount;
    verificationChannel?: PhoneVerificationChannels | '';
    verificationChannels: PhoneVerificationChannels[];
  };
  getServiceTypeByVisitTypeId: (
    visitTypeId?: string,
  ) => ServiceType | undefined;
  getServiceTypeByAbbreviation: (
    abbreviation: string,
  ) => ServiceType | undefined;
  getVisitTypeById: (id: string) => VisitType | undefined;
}

export type ApplicationStore = ApplicationState &
  ApplicationActions &
  AuthSlice &
  FlagsSlice &
  IntakeQuizSlice &
  PatientSlice &
  DevToolsSlice;

const initialState: ApplicationState = {
  fraudData: undefined,
  hasAcceptedTerms: false,
  leadSessionId: undefined,
  patientAccount: undefined,
  patientPassword: undefined,
  reservedVisit: undefined,
  serviceTypes: [],
  referralCode: undefined,
  soonestAvailabilityDate: '',
  stateCode: undefined,
  utmParameters: undefined,
  verificationChannel: undefined,
  verificationChannels: [],
  verificationCode: undefined,
  verificationToken: undefined,
  // TODO: having both visit and visitTypeId seems problematic as we now need to keep track of two pieces of state
  // and make sure they are in sync. we should only keep one piece of state and either derive the visit type id from the
  // visit or add a helper function to do so
  visit: null,
  visitTypeId: null,
};

const useApplicationStore = create<ApplicationStore>()(
  devtools(
    persist(
      (set, get, store) => ({
        ...initialState,
        resetState: () => {
          set(
            () => {
              const { auth, patient } = get();
              auth.clearAuthData();
              patient.resetPatient();
              return { ...initialState };
            },
            false,
            RESET_STATE,
          );
        },
        clearSessionData: () => {
          const { auth, patient } = get();

          auth.clearAuthData();
          patient.clearPatientProfile();
        },
        mergePatientAccount: (params) =>
          set(
            (state) => ({
              patientAccount: { ...state.patientAccount, ...params },
            }),
            false,
            MERGE_PATIENT_ACCOUNT,
          ),
        seedStore: () => seedStore(),
        setFraudData: (data) => set({ fraudData: data }, false, SET_FRAUD_DATA),
        setHasAcceptedTerms: (accepted) =>
          set({ hasAcceptedTerms: accepted }, false, SET_HAS_ACCEPTED_TERMS),
        setLeadSessionId: (leadSessionId) =>
          set({ leadSessionId }, false, SET_LEAD_SESSION_ID),
        setPatientPassword: (patientPassword) =>
          set({ patientPassword }, false, SET_PATIENT_PASSWORD),
        setReservedVisit: (setReservedVisitInput) => {
          const reservedVisit = setReservedVisitInput
            ? ReservedVisitSchema.parse(setReservedVisitInput)
            : undefined;

          set({ reservedVisit }, false, SET_RESERVED_VISIT);
        },
        setShouldSearchNextAvailability: (shouldSearch) =>
          set(
            { shouldSearchNextAvailability: shouldSearch },
            false,
            SET_SEARCH_NEXT_AVAILABILITY,
          ),
        setServiceTypes: (serviceTypes) =>
          set({ serviceTypes }, false, SET_SERVICE_TYPES),
        setSoonestAvailabilityDate: (soonestAvailabilityDate) =>
          set(
            { soonestAvailabilityDate },
            false,
            SET_SOONEST_AVAILABILITY_DATE,
          ),
        setReferralCode: (referralCode) =>
          set({ referralCode }, false, SET_REFERRAL_CODE),
        setStateCode: (stateCode) => set({ stateCode }, false, SET_STATE_CODE),
        setUtmParameters: (utmParameters) =>
          set({ utmParameters }, false, SET_UTM_PARAMETERS),
        setVerificationChannel: (channel) =>
          set(
            { verificationChannel: channel },
            false,
            SET_VERIFICATION_CHANNEL,
          ),
        setVerificationChannels: (channels) =>
          set(
            { verificationChannels: channels },
            false,
            SET_VERIFICATION_CHANNELS,
          ),
        setVerificationCode: (verificationCode) =>
          set({ verificationCode }, false, SET_VERIFICATION_CODE),
        setVerificationToken: (verificationToken) =>
          set({ verificationToken }, false, SET_VERIFICATION_TOKEN),
        setVisit: (visit) => set({ visit }, false, SET_VISIT),
        setVisitTypeId: (visitTypeId) =>
          set({ visitTypeId }, false, SET_VISIT_TYPE_ID),
        getLeadData: () => {
          const {
            leadSessionId,
            patientAccount,
            stateCode,
            utmParameters,
            visitTypeId,
          } = get();
          const {
            address: patientAddress,
            birthDate,
            email,
            firstName,
            gender,
            phoneNumber,
          } = patientAccount || {};
          let address = undefined;

          if (patientAddress?.stateCode) {
            address = { stateCode: patientAddress.stateCode };
          } else if (stateCode) {
            address = { stateCode };
          }

          if (!address || !leadSessionId || !visitTypeId) {
            return;
          }

          return {
            address,
            advertisingCampaign: utmParameters,
            birthDate,
            email,
            firstName,
            gender: gender || undefined,
            sessionId: leadSessionId,
            phoneNumber: phoneNumber?.number,
            visitTypeId,
          };
        },
        getPatientProfile: () => {
          const { patient, patientAccount } = get();

          return patient.patientProfile || patientAccount;
        },
        getSetupAccountState: () => {
          const {
            hasAcceptedTerms,
            patientAccount,
            verificationChannel,
            verificationChannels,
          } = get();

          return {
            hasAcceptedTerms,
            patientAccount,
            verificationChannel,
            verificationChannels,
          };
        },
        getServiceTypeByVisitTypeId: (visitTypeId) => {
          const { serviceTypes } = get();

          return serviceTypes.find((serviceType) =>
            serviceType.visitTypes.some(
              (type) => type.visitTypeId === visitTypeId,
            ),
          );
        },
        getServiceTypeByAbbreviation: (abbreviation) => {
          const { serviceTypes } = get();

          return serviceTypes.find(
            (serviceType) =>
              serviceType.abbreviation.toLowerCase() ===
              abbreviation.toLowerCase(),
          );
        },
        getVisitTypeById: (id) => {
          const { serviceTypes } = get();

          let visitType: VisitType | undefined;

          serviceTypes.forEach((serviceType) => {
            serviceType.visitTypes.forEach((type) => {
              if (type.visitTypeId === id) {
                visitType = type;
                return;
              }
            });
          });

          return visitType;
        },
        ...createAuthSlice(set, get, store),
        ...createFlagsSlice(set, get, store),
        ...createIntakeQuizSlice(set, get, store),
        ...createPatientSlice(set, get, store),
        ...createDevToolsSlice(set, get, store),
      }),
      persistOptions,
    ),
    devtoolsOptions,
  ),
);

declare global {
  interface Window {
    store: ApplicationStore;
    Vouched: any;
    ReactNativeWebView?: { postMessage: (message: string) => void };
  }
}

if (typeof window !== 'undefined' && import.meta.env.MODE !== 'production') {
  (window as Window).store = useApplicationStore.getState();
}

export { useApplicationStore };
